import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, Trash2, Plus, Upload, UserCheck, Pencil, Save, KeyRound, Copy, Archive, UserPlus, Mail, Bell } from "lucide-react";
import api, { ymd } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ContactActions from "@/components/ContactActions";
import EditRecruitModal from "@/components/EditRecruitModal";
import { BroadcastComposer } from "@/components/Broadcast";
import { DeleteConfirm } from "@/components/RecruiterPanel";

const input = "w-full rsp-card px-3 py-2 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] text-sm";

/* ---------------- Approvals ---------------- */
function Approvals() {
  const [pending, setPending] = useState([]);
  const load = () => api.get("/admin/pending-approvals").then((r) => setPending(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { await api.post(`/admin/users/${id}/${action}`); load(); toast.success(action === "deny" ? "User denied & removed." : "User approved."); }
    catch (e) { toast.error("Failed."); }
  };
  return (
    <div className="space-y-3" data-testid="admin-approvals">
      {pending.map((u) => (
        <div key={u.user_id} className="rsp-card p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold truncate">{u.name} <span className="text-xs orange uppercase font-head ml-1">{u.role}{u.role === "admin" && u.admin_title ? ` · ${u.admin_title}` : ""}</span></div>
            <div className="text-[var(--rsp-gray)] text-sm truncate">{u.email}</div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => act(u.user_id, "approve")} data-testid={`approve-${u.user_id}`} className="btn-gold px-3 py-2 flex items-center gap-1"><Check size={15} /> Approve</button>
            <button onClick={() => act(u.user_id, "deny")} data-testid={`deny-${u.user_id}`} className="px-3 py-2 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-red-400 flex items-center gap-1"><X size={15} /> Deny</button>
          </div>
        </div>
      ))}
      {pending.length === 0 && <p className="text-[var(--rsp-muted)]">No pending approvals.</p>}
    </div>
  );
}

/* ---------------- Content ---------------- */
const CONTENT = {
  announcements: { label: "Announcements", get: "/announcements", post: "/admin/announcements", fields: ["title", "body", "priority"], primary: "title", sub: "body" },
  training: { label: "Training", get: "/training", post: "/admin/training", fields: ["title", "date", "end_date", "time", "location", "notes", "category"], primary: "title", sub: "location" },
  regulations: { label: "Regulations", get: "/regulations", post: "/admin/regulations", fields: ["code", "title", "summary", "url"], primary: "title", sub: "summary" },
  weblinks: { label: "Web Links", get: null, post: "/admin/weblinks", fields: ["title", "url", "description", "category"], primary: "title", sub: "url" },
};

function Content() {
  const [kind, setKind] = useState("announcements");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [savingPreview, setSavingPreview] = useState(false);
  const cfg = CONTENT[kind];

  const load = async () => {
    if (kind === "weblinks") {
      const [a, b] = await Promise.all([api.get("/websites"), api.get("/bct")]);
      setItems([...a.data, ...b.data]);
    } else {
      const r = await api.get(cfg.get); setItems(r.data);
    }
  };
  useEffect(() => { setForm({}); setEditingId(null); load().catch(() => {}); /* eslint-disable-next-line */ }, [kind]);

  const save = async () => {
    if (!form[cfg.primary]) return toast.error(`${cfg.primary} required.`);
    try {
      if (editingId) {
        await api.put(`${cfg.post}/${editingId}`, form);
        toast.success("Updated.");
      } else {
        await api.post(cfg.post, form);
        toast.success("Added.");
      }
      setForm({}); setEditingId(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(", ") : "Failed.");
    }
  };
  const startEdit = (it) => {
    const f = {};
    cfg.fields.forEach((k) => { f[k] = it[k] ?? ""; });
    setForm(f); setEditingId(it.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setForm({}); setEditingId(null); };
  const del = async (id) => {
    try { await api.delete(`${cfg.post}/${id}`); if (editingId === id) cancelEdit(); load(); } catch (e) { toast.error("Failed."); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded. URL copied into form.");
      setForm((p) => ({ ...p, image_url: data.url, url: p.url || data.url }));
    } catch (err) { toast.error("Upload failed."); }
  };
  const importSchedule = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post("/admin/training/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview((data.events || []).map((e, idx) => ({ ...e, _key: `${Date.now()}-${idx}` })));
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not read that schedule.");
    } finally { setImporting(false); }
  };
  const setRow = (i, k, v) => setPreview((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const removeRow = (i) => setPreview((p) => p.filter((_, idx) => idx !== i));
  const confirmImport = async () => {
    const valid = (preview || []).filter((r) => (r.title || "").trim()).map(({ _key, ...ev }) => ev);
    if (!valid.length) return toast.error("Add at least one event with a title.");
    setSavingPreview(true);
    try {
      const { data } = await api.post("/admin/training/bulk", { events: valid });
      toast.success(`Added ${data.created} training event${data.created === 1 ? "" : "s"}.`);
      setPreview(null); load();
    } catch (e) {
      toast.error("Could not save events.");
    } finally { setSavingPreview(false); }
  };

  const renderField = (f) => {
    if (f === "priority") return (
      <select key={f} className={input} value={form[f] || ""} onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))} data-testid={`field-${f}`}>
        <option value="">Priority…</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
      </select>);
    if (f === "category") return (
      <select key={f} className={input} value={form[f] || ""} onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))} data-testid={`field-${f}`}>
        <option value="">Category…</option>
        {kind === "training"
          ? ["Drill", "PT", "Briefing", "Weapons", "Field", "Admin", "Ceremony", "Other"].map((c) => <option key={c} value={c}>{c}</option>)
          : (<><option value="military">Military</option><option value="bct">BCT</option></>)}
      </select>);
    if (f === "date" || f === "end_date") return (
      <label key={f} className="block">
        <span className="eyebrow text-[var(--rsp-muted)]" style={{ letterSpacing: "0.12em", fontSize: "0.62rem" }}>
          {f === "date" ? "Start date" : "End date (optional — for multi-day events)"}
        </span>
        <input type="date" className={`${input} mt-1`} value={form[f] || ""} onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))} data-testid={`field-${f}`} />
      </label>);
    return <input key={f} className={input} placeholder={f} value={form[f] || ""} onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))} data-testid={`field-${f}`} />;
  };

  return (
    <div data-testid="admin-content">
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(CONTENT).map(([k, v]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`content-tab-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-head uppercase tracking-wide border ${kind === k ? "bg-[var(--rsp-gold)] text-black border-[var(--rsp-gold)]" : "border-[var(--rsp-border)] text-[var(--rsp-gray)]"}`}>
            {v.label}
          </button>
        ))}
      </div>

      {kind === "training" && (
        <div className="rsp-card p-4 mb-4 space-y-2" data-testid="training-import-card">
          <div className="eyebrow gold" style={{ letterSpacing: "0.14em" }}>Import Training Schedule</div>
          <div className="text-[var(--rsp-gray)] text-xs">Upload a schedule document (PDF, Excel, CSV, text) or a photo/screenshot. AI reads the dates &amp; details and adds each event below — nothing is replaced.</div>
          <label className="btn-gold w-full py-2.5 cursor-pointer flex items-center justify-center gap-2" data-testid="training-import-label">
            <Upload size={16} />
            <input type="file" className="hidden" onChange={importSchedule} data-testid="training-import-input"
              accept=".pdf,.xlsx,.csv,.txt,image/png,image/jpeg,image/webp" disabled={importing} />
            {importing ? "Reading schedule…" : "Upload Schedule Document"}
          </label>
        </div>
      )}

      <div className="rsp-card p-4 mb-4 space-y-2">
        <div className="eyebrow" style={{ letterSpacing: "0.14em" }}>{editingId ? `Edit ${cfg.label.replace(/s$/, "")}` : `New ${cfg.label.replace(/s$/, "")}`}</div>
        {cfg.fields.map(renderField)}
        {kind === "media" && null}
        <div className="flex gap-2">
          <button onClick={save} className="btn-gold flex-1 py-2 flex items-center justify-center gap-1" data-testid="content-create">
            {editingId ? <><Save size={15} /> Save Changes</> : <><Plus size={15} /> Add</>}
          </button>
          {editingId && (
            <button onClick={cancelEdit} className="px-4 py-2 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)]" data-testid="content-cancel-edit">Cancel</button>
          )}
        </div>
      </div>

      <div className="rsp-card p-3 mb-4 flex items-center gap-3">
        <Upload size={18} className="gold" />
        <label className="flex-1 text-sm text-[var(--rsp-gray)] cursor-pointer">
          Upload an image to storage (fills the URL field)
          <input type="file" className="hidden" onChange={upload} data-testid="media-upload-input" accept="image/*" />
        </label>
      </div>

      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className={`rsp-card p-3 flex items-center justify-between gap-2 ${editingId === it.id ? "border-[var(--rsp-gold)]" : ""}`}>
            <div className="min-w-0"><div className="font-semibold truncate">{it[cfg.primary] || it.title}</div>
              <div className="text-[var(--rsp-gray)] text-sm truncate">{it[cfg.sub]}</div></div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => startEdit(it)} data-testid={`edit-content-${it.id}`} className="text-[var(--rsp-muted)] hover:text-[var(--rsp-gold)] p-1"><Pencil size={16} /></button>
              <button onClick={() => del(it.id)} data-testid={`del-content-${it.id}`} className="text-[var(--rsp-muted)] hover:text-red-400 p-1"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="rsp-card w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="training-preview-modal">
            <div className="flex items-center justify-between gap-2 p-4 border-b border-[var(--rsp-border)]">
              <div>
                <div className="font-head uppercase tracking-wide text-lg">Review Imported Schedule</div>
                <div className="text-[var(--rsp-gray)] text-xs">Check the AI's reading, fix anything, remove bad rows — then confirm. Nothing is saved until you confirm.</div>
              </div>
              <button onClick={() => setPreview(null)} className="text-[var(--rsp-muted)] hover:text-[var(--rsp-cream)] shrink-0" data-testid="training-preview-close"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto rsp-scroll p-4 space-y-3">
              {preview.length === 0 && <p className="text-[var(--rsp-muted)] text-sm">No events left. Cancel and try a clearer document.</p>}
              {preview.map((r, i) => (
                <div key={r._key || i} className="rsp-card p-3 bg-[var(--rsp-bg-2)] space-y-2" data-testid={`preview-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <input className={input} placeholder="Title" value={r.title || ""} onChange={(e) => setRow(i, "title", e.target.value)} data-testid={`preview-title-${i}`} />
                    <button onClick={() => removeRow(i)} className="text-[var(--rsp-muted)] hover:text-red-400 shrink-0 p-1" data-testid={`preview-remove-${i}`}><Trash2 size={16} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block"><span className="eyebrow text-[var(--rsp-muted)]" style={{ fontSize: "0.6rem" }}>Start date</span>
                      <input type="date" className={`${input} mt-1`} value={r.date || ""} onChange={(e) => setRow(i, "date", e.target.value)} data-testid={`preview-date-${i}`} /></label>
                    <label className="block"><span className="eyebrow text-[var(--rsp-muted)]" style={{ fontSize: "0.6rem" }}>End date (optional)</span>
                      <input type="date" className={`${input} mt-1`} value={r.end_date || ""} onChange={(e) => setRow(i, "end_date", e.target.value)} data-testid={`preview-end-${i}`} /></label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input className={input} placeholder="Time" value={r.time || ""} onChange={(e) => setRow(i, "time", e.target.value)} data-testid={`preview-time-${i}`} />
                    <select className={input} value={r.category || "Other"} onChange={(e) => setRow(i, "category", e.target.value)} data-testid={`preview-category-${i}`}>
                      {["Drill", "PT", "Briefing", "Weapons", "Field", "Admin", "Ceremony", "Other"].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <input className={input} placeholder="Location" value={r.location || ""} onChange={(e) => setRow(i, "location", e.target.value)} data-testid={`preview-location-${i}`} />
                  <input className={input} placeholder="Notes" value={r.notes || ""} onChange={(e) => setRow(i, "notes", e.target.value)} data-testid={`preview-notes-${i}`} />
                </div>
              ))}
            </div>
            <div className="flex gap-2 p-4 border-t border-[var(--rsp-border)]">
              <button onClick={() => setPreview(null)} className="flex-1 py-2.5 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)]" data-testid="training-preview-cancel">Cancel</button>
              <button onClick={confirmImport} disabled={savingPreview || preview.length === 0} className="btn-gold flex-1 py-2.5 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="training-preview-confirm">
                <Check size={16} /> {savingPreview ? "Saving…" : `Add ${preview.filter((r) => (r.title || "").trim()).length} Event${preview.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Media (device upload only) ---------------- */
function MediaAdmin() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/media", { image_url: data.url, title: "", caption: "" });
      load();
      toast.success("Photo added to media.");
    } catch (err) { toast.error("Upload failed."); }
    finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/admin/media/${id}`); load(); } catch (e) { toast.error("Failed."); } };
  return (
    <div data-testid="admin-media-tab">
      <div className="rsp-card p-4 mb-4">
        <div className="eyebrow mb-2" style={{ letterSpacing: "0.14em" }}>Add Photo</div>
        <label className="btn-gold w-full py-3 flex items-center justify-center gap-2 cursor-pointer" data-testid="media-upload-label">
          <Upload size={16} /> {busy ? "Uploading…" : "Upload from Device"}
          <input type="file" className="hidden" accept="image/*" onChange={upload} data-testid="media-file" disabled={busy} />
        </label>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {items.map((m) => (
          <div key={m.id} className="relative rounded-lg overflow-hidden border border-[var(--rsp-border)] aspect-square bg-[var(--rsp-card)]">
            {m.image_url && <img src={m.image_url.startsWith("http") ? m.image_url : `${process.env.REACT_APP_BACKEND_URL}${m.image_url}?token=${localStorage.getItem("rsp_session_token")}`} alt={m.title || "media"} className="w-full h-full object-cover" />}
            <button onClick={() => del(m.id)} data-testid={`del-media-${m.id}`} className="absolute top-1 right-1 bg-black/60 rounded p-1 text-white hover:text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- MOS ---------------- */
function MosAdmin() {
  const [mos, setMos] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ code: "", title: "", description: "", training_length: "", asvab: "", pay: "" });
  const load = () => api.get("/admin/mos").then((r) => setMos(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.code || !form.title) return toast.error("Code and title required.");
    try { await api.post("/admin/mos", { ...form, videos: [] }); setForm({ code: "", title: "", description: "", training_length: "", asvab: "", pay: "" }); load(); toast.success("MOS saved."); }
    catch (e) { toast.error("Failed."); }
  };
  const del = async (id) => { try { await api.delete(`/admin/mos/${id}`); load(); } catch (e) { toast.error("Failed."); } };
  const filtered = mos.filter((m) => (m.code + m.title).toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="admin-mos">
      <div className="rsp-card p-4 mb-4 space-y-2">
        <div className="eyebrow" style={{ letterSpacing: "0.14em" }}>Add / Update MOS</div>
        <div className="grid grid-cols-2 gap-2">
          <input className={input} placeholder="Code (11B)" value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))} data-testid="mos-code" />
          <input className={input} placeholder="Title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="mos-title" />
        </div>
        <textarea className={input} rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="mos-desc" />
        <div className="grid grid-cols-3 gap-2">
          <input className={input} placeholder="Training length" value={form.training_length} onChange={(e) => setForm((p) => ({ ...p, training_length: e.target.value }))} data-testid="mos-training" />
          <input className={input} placeholder="ASVAB" value={form.asvab} onChange={(e) => setForm((p) => ({ ...p, asvab: e.target.value }))} data-testid="mos-asvab" />
          <input className={input} placeholder="Pay" value={form.pay} onChange={(e) => setForm((p) => ({ ...p, pay: e.target.value }))} data-testid="mos-pay" />
        </div>
        <button onClick={create} className="btn-gold w-full py-2 flex items-center justify-center gap-1" data-testid="mos-create"><Plus size={15} /> Save MOS</button>
      </div>
      <input className={`${input} mb-3`} placeholder="Search MOS…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mos-admin-search" />
      <div className="space-y-2 max-h-[480px] overflow-y-auto rsp-scroll pr-1">
        {filtered.map((m) => (
          <div key={m.id} className="rsp-card p-3 flex items-center justify-between gap-2">
            <div><span className="font-mono-rsp gold mr-2">{m.code}</span>{m.title}</div>
            <button onClick={() => del(m.id)} data-testid={`del-mos-${m.code}`} className="text-[var(--rsp-muted)] hover:text-red-400 shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Create Profile (any role) ---------------- */
const ADMIN_TITLE_OPTS = ["Commander", "1SG", "Readiness NCO", "Training NCO", "Civilian Contractor"];

function CreateProfile() {
  const [form, setForm] = useState({ email: "", name: "", role: "recruit", admin_title: "", phone: "", secondary_email: "" });
  const [busy, setBusy] = useState(false);
  const [taken, setTaken] = useState([]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/admin-title-availability").then((r) => setTaken(r.data.taken || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.email.trim()) return toast.error("Email required.");
    if (form.role === "admin" && !form.admin_title) return toast.error("Select an admin title.");
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim(), name: form.name.trim() || undefined, role: form.role,
        admin_title: form.role === "admin" ? form.admin_title : undefined,
        phone: (form.role !== "admin" ? form.phone.trim() : "") || undefined,
        secondary_email: (form.role === "recruiter" ? form.secondary_email.trim() : "") || undefined,
      };
      const { data } = await api.post("/admin/create-profile", payload);
      toast.success(`${data.role.charAt(0).toUpperCase() + data.role.slice(1)} profile created for ${data.email}. They'll get a sign-in email.`);
      setForm({ email: "", name: "", role: "recruit", admin_title: "", phone: "", secondary_email: "" });
      api.get("/admin-title-availability").then((r) => setTaken(r.data.taken || [])).catch(() => {});
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create profile.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-create-profile">
      <div className="rsp-card p-4 space-y-3">
        <div className="flex items-center gap-2"><UserPlus size={18} className="gold" /><span className="font-head uppercase tracking-wide">Create a Profile</span></div>
        <div className="text-[var(--rsp-gray)] text-xs">Enter an email and pick a role. The person signs in with Google (or sets a password) using this email and lands in the right role.</div>
        <input className={input} placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="create-profile-email" />
        <input className={input} placeholder="Full name (optional)" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="create-profile-name" />
        <div>
          <span className="eyebrow" style={{ letterSpacing: "0.14em" }}>Role</span>
          <select className={`${input} mt-1.5`} value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="create-profile-role">
            <option value="recruit">Recruit</option>
            <option value="recruiter">Recruiter (Cadre)</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {form.role === "admin" && (
          <div>
            <span className="eyebrow" style={{ letterSpacing: "0.14em" }}>Admin Title</span>
            <select className={`${input} mt-1.5`} value={form.admin_title} onChange={(e) => set("admin_title", e.target.value)} data-testid="create-profile-title">
              <option value="">Select a title</option>
              {ADMIN_TITLE_OPTS.map((t) => (
                <option key={t} value={t} disabled={taken.includes(t)}>{t}{taken.includes(t) ? " — taken" : ""}</option>
              ))}
            </select>
          </div>
        )}
        {form.role !== "admin" && (
          <input className={input} placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="create-profile-phone" />
        )}
        {form.role === "recruiter" && (
          <input className={input} placeholder="Secondary contact email (recruits email this)" value={form.secondary_email} onChange={(e) => set("secondary_email", e.target.value)} data-testid="create-profile-secondary-email" />
        )}
        <button onClick={submit} disabled={busy} className="btn-gold w-full py-2.5 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="create-profile-submit">
          <UserPlus size={16} /> {busy ? "Creating…" : "Create Profile"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Recruiters ---------------- */
function Recruiters() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", secondary_email: "", phone: "", bio: "", photo_url: "", url: "" });
  const load = () => api.get("/admin/recruiters").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name) return toast.error("Name required.");
    try { await api.post("/admin/recruiters", form); setForm({ name: "", email: "", secondary_email: "", phone: "", bio: "", photo_url: "", url: "" }); load(); toast.success("Recruiter saved."); }
    catch (e) { toast.error("Failed."); }
  };
  const del = async (id) => { try { await api.delete(`/admin/recruiters/${id}`); load(); } catch (e) { toast.error("Failed."); } };
  return (
    <div data-testid="admin-recruiters">
      <div className="rsp-card p-4 mb-4 space-y-2">
        <div className="eyebrow" style={{ letterSpacing: "0.14em" }}>Pre-create Recruiter (bind by verified Google email)</div>
        <input className={input} placeholder="Name (e.g. SFC Martinez)" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} data-testid="rec-adm-name" />
        <input className={input} placeholder="Verified Google (login) email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} data-testid="rec-adm-email" />
        <input className={input} placeholder="Secondary contact email (recruits email this)" value={form.secondary_email} onChange={(e) => setForm((p) => ({ ...p, secondary_email: e.target.value }))} data-testid="rec-adm-secondary-email" />
        <div className="grid grid-cols-2 gap-2">
          <input className={input} placeholder="Phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} data-testid="rec-adm-phone" />
          <input className={input} placeholder="Photo URL" value={form.photo_url} onChange={(e) => setForm((p) => ({ ...p, photo_url: e.target.value }))} data-testid="rec-adm-photo" />
        </div>
        <button onClick={create} className="btn-gold w-full py-2 flex items-center justify-center gap-1" data-testid="rec-adm-create"><Plus size={15} /> Add Recruiter</button>
      </div>
      <div className="space-y-2">
        {list.map((r) => (
          <div key={r.id} className="rsp-card p-3 flex items-center justify-between gap-2">
            <div className="min-w-0"><div className="font-semibold truncate">{r.name}</div>
              <div className="text-[var(--rsp-gray)] text-sm truncate">{r.email} {r.phone ? `· ${r.phone}` : ""}</div></div>
            <button onClick={() => del(r.id)} data-testid={`del-rec-${r.id}`} className="text-[var(--rsp-muted)] hover:text-red-400 shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Recruits ---------------- */
export function RecruitsAdmin() {
  const [groups, setGroups] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const [editRecruit, setEditRecruit] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const load = () => api.get("/admin/by-recruiter").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/recruiters").then((r) => setRecruiters(r.data)).catch(() => {}); }, []);

  const allIds = groups.flatMap((g) => g.recruits.map((u) => u.user_id));
  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((prev) => (prev.size === allIds.length && allIds.length > 0 ? new Set() : new Set(allIds)));
  const clearSel = () => setSelected(new Set());

  const assign = async (recruit_id, recruiter) => {
    try { await api.post(`/admin/recruits/${recruit_id}/recruiter`, { recruiter: recruiter || null }); load(); toast.success("Updated."); }
    catch (e) { toast.error("Failed."); }
  };
  const setAit = async (recruit_id, val) => {
    try { await api.put(`/me/recruits/${recruit_id}/ait`, { ait_end_date: val || null }); load(); toast.success("AIT end date saved."); }
    catch (e) { toast.error("Could not save AIT date."); }
  };
  const doDelete = async () => {
    const u = confirmDel; setConfirmDel(null);
    try { await api.delete(`/me/recruits/${u.user_id}`); load(); toast.success(`${u.name} deleted.`); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not delete recruit."); }
  };
  const bulkAssign = async (recruiter) => {
    try {
      const { data } = await api.post("/admin/recruits/bulk-assign", { recruit_ids: [...selected], recruiter: recruiter || null });
      clearSel(); load(); toast.success(`Reassigned ${data.updated} recruit${data.updated === 1 ? "" : "s"}.`);
    } catch (e) { toast.error("Bulk reassign failed."); }
  };
  const bulkDelete = async () => {
    setBulkDelOpen(false);
    try {
      const { data } = await api.post("/admin/recruits/bulk-delete", { recruit_ids: [...selected] });
      clearSel(); load(); toast.success(`Deleted ${data.deleted} recruit${data.deleted === 1 ? "" : "s"}.`);
    } catch (e) { toast.error("Bulk delete failed."); }
  };
  return (
    <div data-testid="admin-recruits" className="space-y-5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-[var(--rsp-gray)] cursor-pointer" data-testid="bulk-select-all">
          <input type="checkbox" checked={allIds.length > 0 && selected.size === allIds.length} onChange={toggleAll} />
          Select all ({allIds.length})
        </label>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap rsp-card px-3 py-2" data-testid="bulk-action-bar">
            <span className="text-sm gold font-head uppercase tracking-wide" data-testid="bulk-count">{selected.size} selected</span>
            <select className={`${input} w-auto`} defaultValue="" onChange={(e) => { if (e.target.value !== "") { bulkAssign(e.target.value === "__unassign__" ? null : e.target.value); e.target.value = ""; } }} data-testid="bulk-assign-select">
              <option value="">Reassign to…</option>
              <option value="__unassign__">Unassigned</option>
              {recruiters.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
            </select>
            <button onClick={() => setBulkDelOpen(true)} data-testid="bulk-delete-btn"
              className="text-sm px-3 py-2 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-red-400 hover:border-red-400/60 flex items-center gap-1 transition-colors">
              <Trash2 size={15} /> Delete
            </button>
            <button onClick={clearSel} className="text-sm px-3 py-2 rounded text-[var(--rsp-muted)] hover:text-[var(--rsp-cream)]" data-testid="bulk-clear">Clear</button>
          </div>
        )}
      </div>
      {groups.map((g) => (
        <div key={g.recruiter}>
          <div className="eyebrow mb-2 flex items-center gap-2" style={{ letterSpacing: "0.14em" }}>
            <UserCheck size={14} /> {g.recruiter} · {g.recruits.length}
          </div>
          <div className="space-y-2">
            {g.recruits.map((u) => (
              <div key={u.user_id} className={`rsp-card p-3 space-y-2 ${selected.has(u.user_id) ? "border-[var(--rsp-gold)]/70" : ""}`} data-testid={`admin-recruit-${u.user_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <input type="checkbox" checked={selected.has(u.user_id)} onChange={() => toggle(u.user_id)} data-testid={`bulk-check-${u.user_id}`} />
                    <div className="min-w-0"><div className="font-semibold truncate">{u.name}</div><div className="text-[var(--rsp-gray)] text-sm truncate">{u.email}{u.mos ? ` · ${u.mos}` : ""}</div></div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <ContactActions phone={u.phone} email={u.contact_email || u.email} name={u.name} />
                    <select className={`${input} w-auto`} defaultValue={g.recruiter === "Unassigned" ? "" : g.recruiter} onChange={(e) => assign(u.user_id, e.target.value)} data-testid={`assign-${u.user_id}`}>
                      <option value="">Unassigned</option>
                      {recruiters.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </select>
                    <button onClick={() => setEditRecruit(u)} data-testid={`admin-edit-recruit-${u.user_id}`}
                      className="p-2 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)] hover:border-[var(--rsp-gold)]/60 transition-colors" title={`Edit ${u.name}`}>
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => setConfirmDel(u)} data-testid={`admin-del-recruit-${u.user_id}`}
                      className="p-2 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-red-400 hover:border-red-400/60 transition-colors" title={`Delete ${u.name}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="eyebrow shrink-0" style={{ letterSpacing: "0.12em" }}>Ship Date</span>
                    <span className="text-[var(--rsp-cream)]" data-testid={`admin-ship-date-${u.user_id}`}>
                      {u.ship_date ? ymd(u.ship_date) : "—"}
                    </span>
                    {u.ship_date && (() => {
                      const d = Math.ceil((new Date(u.ship_date).getTime() - Date.now()) / 86400000);
                      return d <= 0 ? <span className="text-xs font-head uppercase px-2 py-0.5 rounded border text-emerald-400 border-emerald-400/40" data-testid={`admin-ship-badge-${u.user_id}`}>Has Shipped</span> : null;
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="eyebrow shrink-0" style={{ letterSpacing: "0.12em" }}>AIT End Date</span>
                    <span className="text-[var(--rsp-cream)]" data-testid={`admin-ait-date-${u.user_id}`}>{u.ait_end_date ? ymd(u.ait_end_date) : "—"}</span>
                  </div>
                </div>
              </div>
            ))}
            {g.recruits.length === 0 && <p className="text-[var(--rsp-muted)] text-sm">No recruits.</p>}
          </div>
        </div>
      ))}
      {groups.length === 0 && <p className="text-[var(--rsp-muted)]">No recruits yet.</p>}
      {confirmDel && <DeleteConfirm name={confirmDel.name} onCancel={() => setConfirmDel(null)} onConfirm={doDelete} />}
      {editRecruit && <EditRecruitModal recruit={editRecruit} recruiters={recruiters} onClose={() => setEditRecruit(null)} onSaved={load} />}
      {bulkDelOpen && <DeleteConfirm name={`${selected.size} selected recruit${selected.size === 1 ? "" : "s"}`} onCancel={() => setBulkDelOpen(false)} onConfirm={bulkDelete} />}
    </div>
  );
}

/* ---------------- Titles (cadre slot management) ---------------- */
function TitlesAdmin() {
  const [rows, setRows] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const load = () => api.get("/admin/admin-titles").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const release = async () => {
    const t = confirm; setConfirm(null);
    try { await api.post("/admin/admin-titles/release", { title: t.title }); load(); toast.success(`${t.title} released.`); }
    catch (e) { toast.error("Could not release title."); }
  };
  return (
    <div data-testid="admin-titles" className="space-y-2">
      <p className="text-[var(--rsp-muted)] text-sm mb-2">Each cadre title is held by one admin. Release a title to free it up (revokes that admin's access) so it can be selected again.</p>
      {rows.map((r) => (
        <div key={r.title} className="rsp-card p-3 flex items-center justify-between gap-2" data-testid={`title-row-${r.title}`}>
          <div className="min-w-0">
            <div className="font-head uppercase tracking-wide">{r.title}</div>
            <div className="text-sm truncate">{r.holder ? <span className="text-[var(--rsp-cream)]">{r.holder.name} · <span className="text-[var(--rsp-gray)]">{r.holder.email}</span></span> : <span className="gold">Available</span>}</div>
          </div>
          {r.holder && (
            <button onClick={() => setConfirm(r)} data-testid={`release-${r.title}`}
              className="text-sm px-3 py-2 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-red-400 hover:border-red-400/60 transition-colors">Release</button>
          )}
        </div>
      ))}
      {confirm && <DeleteConfirm name={`the ${confirm.title} title (revokes ${confirm.holder?.name}'s admin access)`} onCancel={() => setConfirm(null)} onConfirm={release} />}
    </div>
  );
}

/* ---------------- Accounts (password reset + auto-purge) ---------------- */
function AccountsAdmin() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [days, setDays] = useState(30);
  const [savingDays, setSavingDays] = useState(false);
  const [purging, setPurging] = useState(false);
  const [welcomeEmail, setWelcomeEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [notifyPreview, setNotifyPreview] = useState(null);

  useEffect(() => { api.get("/admin/settings").then((r) => setDays(r.data.archive_purge_days)).catch(() => {}); }, []);

  const genLink = async () => {
    if (!email.trim()) return toast.error("Enter a user's email.");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/password-reset-link", { email: email.trim(), base_url: window.location.origin });
      setResult(data);
      toast.success(data.emailed ? "Reset link generated & emailed." : "Reset link generated.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not generate reset link.");
    } finally { setBusy(false); }
  };
  const copyLink = async () => {
    if (!result?.link) return;
    try {
      await navigator.clipboard.writeText(result.link);
      toast.success("Link copied to clipboard.");
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = result.link; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Link copied to clipboard."); }
      catch (err) { toast.error("Couldn't copy — select the link and copy manually."); }
      ta.remove();
    }
  };
  const saveDays = async () => {
    setSavingDays(true);
    try { const { data } = await api.put("/admin/settings", { archive_purge_days: Number(days) }); setDays(data.archive_purge_days); toast.success("Auto-purge setting saved."); }
    catch (e) { toast.error("Could not save setting."); }
    finally { setSavingDays(false); }
  };
  const purgeNow = async () => {
    setPurging(true);
    try { const { data } = await api.post("/admin/purge-shipped"); toast.success(`Purged ${data.deleted} shipped recruit${data.deleted === 1 ? "" : "s"}.`); }
    catch (e) { toast.error("Purge failed."); }
    finally { setPurging(false); }
  };
  const resendWelcome = async () => {
    if (!welcomeEmail.trim()) return toast.error("Enter a user's email.");
    setResending(true);
    try {
      const { data } = await api.post("/admin/resend-welcome", { email: welcomeEmail.trim() });
      toast.success(`Sign-in email re-sent to ${data.name || data.email}.`);
      setWelcomeEmail("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not re-send the email.");
    } finally { setResending(false); }
  };
  const notifyAll = async () => {
    setNotifying(true);
    try {
      const { data } = await api.get("/admin/notify-incomplete/preview");
      setNotifyPreview(data);
    } catch (e) {
      toast.error("Could not load the recipient list.");
    } finally { setNotifying(false); }
  };
  const confirmNotify = async () => {
    setNotifying(true);
    try {
      const { data } = await api.post("/admin/notify-incomplete");
      toast.success(data.sent ? `Welcome email sent to ${data.sent} recruit${data.sent === 1 ? "" : "s"}.` : (data.message || "No recruits with incomplete profiles were found."));
      setNotifyPreview(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send notifications.");
    } finally { setNotifying(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-accounts">
      <div className="rsp-card p-5">
        <div className="flex items-center gap-2 mb-1"><KeyRound className="gold" size={20} /><div className="font-head uppercase tracking-wide">Password Reset</div></div>
        <p className="text-[var(--rsp-gray)] text-sm mb-3">Generate a reset link for any user who can't reach their email. Copy and share it directly — it's also emailed as a backup. Link expires in 1 hour.</p>
        <div className="flex flex-wrap gap-2">
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="User email"
            className={`${input} flex-1 min-w-[200px]`} data-testid="reset-user-email" />
          <button onClick={genLink} disabled={busy} className="btn-gold px-4 py-2 disabled:opacity-60" data-testid="reset-generate-btn">
            {busy ? "Generating…" : "Generate Link"}
          </button>
        </div>
        {result && (
          <div className="mt-3 rsp-card p-3 bg-[var(--rsp-bg-2)]" data-testid="reset-link-result">
            <div className="text-xs eyebrow mb-1" style={{ letterSpacing: "0.12em" }}>Reset link for {result.name || result.email}{result.emailed ? " · emailed" : ""}</div>
            <div className="flex items-center gap-2">
              <input readOnly value={result.link} className={`${input} flex-1 text-xs`} data-testid="reset-link-value" onFocus={(e) => e.target.select()} />
              <button onClick={copyLink} className="px-3 py-2 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)] hover:border-[var(--rsp-gold)]/60 flex items-center gap-1" data-testid="reset-copy-btn"><Copy size={14} /> Copy</button>
            </div>
          </div>
        )}
      </div>

      <div className="rsp-card p-5">
        <div className="flex items-center gap-2 mb-1"><Mail className="gold" size={20} /><div className="font-head uppercase tracking-wide">Resend Welcome Email</div></div>
        <p className="text-[var(--rsp-gray)] text-sm mb-3">Missed the "your profile is ready" email? Re-send the branded sign-in email (with how-to-sign-in steps) to any recruit, recruiter, or admin. Or tap <strong className="text-[var(--rsp-cream)]">Notify All</strong> to email the welcome message to every recruit with an incomplete profile (missing ship date, MOS, or rank) — once every 24 hours.</p>
        <div className="flex flex-wrap gap-2">
          <input value={welcomeEmail} onChange={(e) => setWelcomeEmail(e.target.value)} type="email" placeholder="User email"
            className={`${input} flex-1 min-w-[200px]`} data-testid="resend-welcome-email" />
          <button onClick={resendWelcome} disabled={resending} className="btn-gold px-4 py-2 flex items-center gap-1 disabled:opacity-60" data-testid="resend-welcome-btn">
            <Mail size={15} /> {resending ? "Sending…" : "Resend Email"}
          </button>
          <button onClick={notifyAll} disabled={notifying} title="Email the welcome message to all recruits with an incomplete profile (once per 24h)"
            className="btn-gold px-4 py-2 flex items-center gap-1 disabled:opacity-60" data-testid="notify-all-btn">
            <Bell size={15} /> {notifying ? "Sending…" : "Notify All"}
          </button>
        </div>
      </div>

      <div className="rsp-card p-5">
        <div className="flex items-center gap-2 mb-1"><Archive className="gold" size={20} /><div className="font-head uppercase tracking-wide">Auto-Purge Shipped Recruits</div></div>
        <p className="text-[var(--rsp-gray)] text-sm mb-3">Permanently delete recruits this many days after their ship date, so archives don't grow forever. Runs automatically every hour. Set to <strong>0</strong> to disable.</p>
        <div className="flex flex-wrap items-center gap-2">
          <input type="number" min="0" value={days} onChange={(e) => setDays(e.target.value)} className={`${input} w-24`} data-testid="purge-days-input" />
          <span className="text-[var(--rsp-gray)] text-sm">days after ship date</span>
          <button onClick={saveDays} disabled={savingDays} className="btn-gold px-4 py-2 flex items-center gap-1 disabled:opacity-60" data-testid="purge-save-btn"><Save size={15} /> {savingDays ? "Saving…" : "Save"}</button>
          <button onClick={purgeNow} disabled={purging} className="px-4 py-2 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-red-400 hover:border-red-400/60 flex items-center gap-1 disabled:opacity-60" data-testid="purge-now-btn"><Trash2 size={15} /> {purging ? "Purging…" : "Run purge now"}</button>
        </div>
      </div>

      {notifyPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setNotifyPreview(null)}>
          <div className="rsp-card w-full max-w-md max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="notify-preview-modal">
            <div className="flex items-center justify-between gap-2 p-4 border-b border-[var(--rsp-border)]">
              <div className="flex items-center gap-2"><Bell size={18} className="gold" /><span className="font-head uppercase tracking-wide">Notify All</span></div>
              <button onClick={() => setNotifyPreview(null)} className="text-[var(--rsp-muted)] hover:text-[var(--rsp-cream)]" data-testid="notify-preview-close"><X size={20} /></button>
            </div>
            <div className="p-4">
              <p className="text-sm text-[var(--rsp-gray)]">
                {notifyPreview.count > 0
                  ? <>The <strong className="text-[var(--rsp-cream)]">Welcome to RSP Riverside</strong> email will be sent to <strong className="gold" data-testid="notify-preview-count">{notifyPreview.count}</strong> recruit{notifyPreview.count === 1 ? "" : "s"} with an incomplete profile:</>
                  : <>No recruits with an incomplete profile were found — nothing to send.</>}
              </p>
              {notifyPreview.throttled && (
                <div className="mt-2 text-xs text-amber-400" data-testid="notify-preview-throttle">Already sent recently — available again in {notifyPreview.available_in}.</div>
              )}
            </div>
            {notifyPreview.count > 0 && (
              <div className="flex-1 overflow-y-auto rsp-scroll px-4 pb-2 space-y-1.5" data-testid="notify-preview-list">
                {notifyPreview.recruits.map((r) => (
                  <div key={r.email} className="rsp-card p-2.5 bg-[var(--rsp-bg-2)] flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-[var(--rsp-cream)] truncate">{r.name}</div>
                      <div className="text-xs text-[var(--rsp-muted)] truncate">{r.email}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-amber-400/80 shrink-0 text-right">missing {r.missing.join(", ")}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 p-4 border-t border-[var(--rsp-border)]">
              <button onClick={() => setNotifyPreview(null)} className="flex-1 py-2.5 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)]" data-testid="notify-preview-cancel">Cancel</button>
              <button onClick={confirmNotify} disabled={notifying || notifyPreview.count === 0 || notifyPreview.throttled}
                className="btn-gold flex-1 py-2.5 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="notify-preview-confirm">
                <Mail size={16} /> {notifying ? "Sending…" : `Send to ${notifyPreview.count}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[var(--rsp-bg)] flex justify-center" data-testid="admin-page">
      <div className="w-full max-w-[820px] px-5 py-5">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/")} data-testid="admin-back" className="w-9 h-9 rounded-lg border border-[var(--rsp-border)] flex items-center justify-center text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)]"><ArrowLeft size={18} /></button>
          <div><div className="eyebrow gold" style={{ letterSpacing: "0.16em" }}>RSP · Riverside</div><h1 className="heading text-3xl -mt-0.5">Admin CMS</h1></div>
        </div>
        <Tabs defaultValue="approvals">
          <TabsList className="flex flex-wrap gap-2 bg-transparent p-0 h-auto mb-5">
            {["approvals", "create", "content", "media", "mos", "recruiters", "recruits", "titles", "messages", "accounts"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`}
                className="px-4 py-2 rounded-lg border border-[var(--rsp-border)] font-head uppercase tracking-wide data-[state=active]:bg-[var(--rsp-gold)] data-[state=active]:text-black">
                {t === "create" ? "Create Profile" : t}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="approvals"><Approvals /></TabsContent>
          <TabsContent value="create"><CreateProfile /></TabsContent>
          <TabsContent value="content"><Content /></TabsContent>
          <TabsContent value="media"><MediaAdmin /></TabsContent>
          <TabsContent value="mos"><MosAdmin /></TabsContent>
          <TabsContent value="recruiters"><Recruiters /></TabsContent>
          <TabsContent value="recruits"><RecruitsAdmin /></TabsContent>
          <TabsContent value="titles"><TitlesAdmin /></TabsContent>
          <TabsContent value="messages"><BroadcastComposer /></TabsContent>
          <TabsContent value="accounts"><AccountsAdmin /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
